import { useState, useCallback } from 'react';

const STORAGE_KEY = 'rc_guest_progress';

const getTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

const loadProgress = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { thoughtCount: 0, talkCount: 0 };
    const data = JSON.parse(raw);
    // Reset if it's a new day
    if (data.date !== getTodayStr()) return { thoughtCount: 0, talkCount: 0 };
    return { thoughtCount: data.thoughtCount || 0, talkCount: data.talkCount || 0 };
  } catch {
    return { thoughtCount: 0, talkCount: 0 };
  }
};

const saveProgress = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, date: getTodayStr() }));
  } catch {}
};

export const useGuestMotivation = (isAuthenticated) => {
  const [counts, setCounts] = useState(loadProgress);
  // Session-level dismissal states (reset on page refresh)
  const [valueMomentDismissed, setValueMomentDismissed] = useState(false);
  const [softGatePassed, setSoftGatePassed] = useState(false);

  const { thoughtCount, talkCount } = counts;

  const incrementThought = useCallback(() => {
    if (isAuthenticated) return;
    setCounts(prev => {
      const next = { ...prev, thoughtCount: prev.thoughtCount + 1 };
      saveProgress(next);
      return next;
    });
  }, [isAuthenticated]);

  const incrementTalk = useCallback(() => {
    if (isAuthenticated) return;
    setCounts(prev => {
      const next = { ...prev, thoughtCount: prev.thoughtCount + 1, talkCount: prev.talkCount + 1 };
      saveProgress(next);
      return next;
    });
  }, [isAuthenticated]);

  const dismissValueMoment = useCallback(() => setValueMomentDismissed(true), []);
  const passSoftGate = useCallback(() => setSoftGatePassed(true), []);

  // Phase logic (only applies when not authenticated)
  const active = !isAuthenticated;

  // Value moment: shows when on thought 4+ (count >= 3), once per session
  const showValueMoment = active && thoughtCount >= 3 && !valueMomentDismissed && !softGatePassed;

  // Streak chip: persists after value moment dismissed, or naturally from thought 5+
  const showStreakChip = active && thoughtCount >= 3 && (valueMomentDismissed || thoughtCount >= 4);

  // Soft gate: blocks next load at thought 8+ (count >= 7) until passed
  const needsSoftGate = (newCount) => active && newCount >= 7 && !softGatePassed;

  return {
    thoughtCount,
    talkCount,
    showValueMoment,
    showStreakChip,
    needsSoftGate,
    incrementThought,
    incrementTalk,
    dismissValueMoment,
    passSoftGate,
  };
};
